import { Component, inject, ChangeDetectionStrategy } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { MatIconModule } from '@angular/material/icon';
import { TextInputComponent } from '@olv/ui-shared/ui-shared/text-input/text-input.component';
import { FormErrorService } from '@olv/ui-shared/services/form-error.service';
import { ValidationService } from '@olv/ui-shared/services/validation.service';
import { NetworkMetadataService } from '@olv/ui-shared/services/network-metadata.service';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';

export interface CreateReversalDialogResult {
  destinationWalletAddress: string;
}

export interface CreateReversalDialogData {
  network: string;
}

@Component({
  selector: 'app-create-reversal-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MatIconModule, TextInputComponent, ButtonComponent],
  templateUrl: './create-reversal-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./create-reversal-dialog.component.scss'],
})
export class CreateReversalDialogComponent {
  private readonly fb = inject(FormBuilder);
  private readonly formErrorService = inject(FormErrorService);
  private readonly dialogRef =
    inject<MatDialogRef<CreateReversalDialogComponent, CreateReversalDialogResult>>(MatDialogRef);
  data = inject<CreateReversalDialogData>(MAT_DIALOG_DATA);

  readonly reversalForm: FormGroup;
  private readonly metadata = inject(NetworkMetadataService);

  constructor() {
    this.reversalForm = this.fb.group({
      destinationWalletAddress: [
        '',
        [Validators.required, ValidationService.addressValidator(() => this.metadata.networks(), this.data.network)],
      ],
    });
    this.metadata
      .whenLoaded()
      .subscribe(() => this.reversalForm.get('destinationWalletAddress')?.updateValueAndValidity({ emitEvent: false }));
  }

  getFieldError(controlName: string, fieldName: string): string {
    return this.formErrorService.getErrorMessage(this.reversalForm.get(controlName), fieldName);
  }

  get nextButtonState(): 'disabled' | 'default' {
    return this.reversalForm.invalid ? 'disabled' : 'default';
  }

  onSubmit(): void {
    if (this.reversalForm.invalid) {
      this.reversalForm.markAllAsTouched();
      return;
    }

    this.dialogRef.close({
      destinationWalletAddress: this.reversalForm.value.destinationWalletAddress.trim(),
    });
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
